// The universal residual stage.
//
// Every source pipeline ends with one uniform stage that can absorb
// ANYTHING because it executes client-side: it owns the residual
// op-replay path and the generic value-collapse fallback. It is the last
// stage in the plan-time fold — it wraps no source; it only turns
// whatever the provider did NOT absorb into ordinary `PlanNode`s that
// replay through the existing `evalOpCall` machinery at materialize time,
// and supplies the default rule for collapsing a dataset-valued
// comparison operand to a literal when the provider has no `collapseRef`.
//
// Residual `PlanNode` shapes mirror the standalone ops with `params[0]`
// (the dataset) stripped — the replay loop re-prepends the threaded
// dataset before re-dispatching. A source op's trailing forms are already
// written WITHOUT a leading dataset positional, so their params are used
// as-is.

import type { SourcePos } from '../ast.ts';
import { UncollapsibleRefError } from '../errors.ts';
import { type AstInput, astShape, embeddedString } from '../normalize.ts';
import type { PlanNode } from '../plan.ts';
import { projectSchema, type Schema } from '../schema/types.ts';
import type { Cell, MaterializedDataset } from '../value.ts';

/**
 * Build the residual client-side `filter` `PlanNode` for a non-pushable
 * predicate subtree. The predicate is wrapped in a `(fn [%] <residual>)`
 * lambda so it matches the standalone `filter`'s `[ds, fn]` shape; the
 * row-context column compiler unwraps the body. `filter` is
 * schema-preserving, so `schemaAfter` is the input schema unchanged.
 *
 * Shared SSOT for "wrap a residual predicate as a replay node" — the CDF
 * op and every provider's generic residual stage produce the identical
 * node so a residual filter replays uniformly.
 */
export const residualFilterNode = (residual: AstInput, schema: Schema): PlanNode => ({
  op: 'filter',
  args: { name: 'filter', params: [{ name: 'fn', params: [[{ $sym: '%' }], residual] }] },
  schemaAfter: schema,
});

/**
 * Re-emit a non-absorbed `(sort …)` constraint as a residual `PlanNode`.
 * The decoded `sort` is the ORIGINAL form verbatim (its lambda keys +
 * unsortable-type guards intact); its params are already dataset-less, so
 * they replay as-is. `sort` is schema-preserving.
 */
export const residualSortNode = (sortForm: AstInput, schema: Schema): PlanNode => {
  const shape = astShape(sortForm);
  if (shape.kind !== 'opCall' || shape.call.name !== 'sort') {
    throw new TypeError('residualSortNode expects a (sort …) op-call form');
  }
  return {
    op: 'sort',
    args: { name: 'sort', params: [...(shape.call.params ?? [])] },
    schemaAfter: schema,
  };
};

/**
 * Re-emit a non-absorbed `(columns …)` projection as a residual
 * `PlanNode`. `schemaAfter` is the input schema projected to the
 * requested names via the engine's projection SSOT, so the declared and
 * replayed schemas cannot diverge. `columnsForm` carries the raw column
 * vector AST verbatim for replay.
 */
export const residualColumnsNode = (columnsForm: AstInput, columns: readonly string[], schema: Schema): PlanNode => ({
  op: 'columns',
  args: { name: 'columns', params: [columnsForm] },
  schemaAfter: projectSchema(schema, columns),
});

/**
 * Re-emit a non-absorbed `(limit n)` as a residual `PlanNode`. `limit`
 * is schema-preserving (it drops rows, not columns), so `schemaAfter` is
 * the input schema unchanged.
 */
export const residualLimitNode = (limit: number, schema: Schema): PlanNode => ({
  op: 'limit',
  args: { name: 'limit', params: [limit] },
  schemaAfter: schema,
});

/**
 * The generic value-collapse fallback for a dataset-valued comparison
 * operand, used when the source op's provider supplies no `collapseRef`.
 * The nested-sub-query pre-pass has already materialised the inner
 * query; this rule picks the single column whose values are inlined:
 *
 *  - an explicit `(col <inner-query> <name>)` projection names the column
 *    directly — honoured even on a multi-column inner result;
 *  - otherwise, an inner result with exactly ONE column collapses to it
 *    (single-column inference);
 *  - a multi-column inner result with no explicit projection is
 *    ambiguous → `UncollapsibleRefError`.
 *
 * `rhsNode` is the raw RHS AST (so an explicit `(col …)` projection can
 * be recognised); `rhs` is its already-materialised dataset value. The
 * type-shape guard (a chosen column that is itself nested/struct/TS) is
 * enforced by the caller at collapse time, not here.
 */
export const genericCollapseColumn = (rhsNode: AstInput, rhs: MaterializedDataset, pos: SourcePos): string => {
  const explicit = explicitProjectionColumn(rhsNode, rhs.schema);
  if (explicit !== undefined) {
    return explicit;
  }
  const names = [...rhs.schema.keys()];
  if (names.length === 1) {
    return names[0] as string;
  }
  throw new UncollapsibleRefError(names, pos);
};

/**
 * If `rhsNode` is an explicit `(col <inner> <name>)` projection naming a
 * column present on `schema`, return that column name. The LAST label is
 * the projected column. The head (first param) is the inner-query
 * EXPRESSION — a `(query …)` op-call, not a literal — so `colNodeLabels`
 * (which requires every label to be a literal) cannot read this node; the
 * projected name is extracted directly from the trailing param instead.
 * Returns `undefined` when the node is not a multi-arg `(col …)` form, the
 * trailing param is not a name literal, or it names a column absent from
 * the inner schema.
 */
const explicitProjectionColumn = (rhsNode: AstInput, schema: Schema): string | undefined => {
  const shape = astShape(rhsNode);
  if (shape.kind !== 'opCall' || shape.call.name !== 'col') {
    return undefined;
  }
  const params = shape.call.params ?? [];
  if (params.length < 2) {
    return undefined;
  }
  const projected = nameLiteral(params.at(-1) as AstInput);
  return projected !== undefined && schema.has(projected) ? projected : undefined;
};

/** The string name a keyword / symbol / string-literal AST node denotes, or `undefined` for anything else. */
const nameLiteral = (node: AstInput): string | undefined => {
  const s = astShape(node);
  if (s.kind === 'keyword') {
    return s.name;
  }
  if (s.kind === 'symbolRef') {
    return s.sym;
  }
  return embeddedString(node);
};

/** The literal RHS to inline for a collapsed dataset operand: the chosen column's distinct values. */
export const collapseToValues = (rhs: MaterializedDataset, column: string): readonly Cell[] => {
  const cells = rhs.columns[column] ?? [];
  return [...new Set(cells)];
};
